import { isSalesQuotedModule, MODULES, type ModuleDef } from '@estac1/shared';
import { Badge, Button, ConfirmDialog, toast, useConfirm } from '@estac1/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api } from '../../lib/api.js';
import { type ClientOverview, day } from './types.js';

const MODULE_STATUS: Record<string, string> = {
  active: 'ativo',
  trialing: 'em teste',
  canceled: 'desligado',
  off: 'desligado',
};

/** Ligados pelo dono no painel, porque precisam de configuração antes. */
const OWNER_ONLY_ENABLE = new Set(['online_checkout', 'nfse']);

const isOn = (status: string) => status === 'active' || status === 'trialing';

const effect = (def: ModuleDef, turningOn: boolean) => {
  if (!turningOn) {
    return def.billing.kind === 'flat'
      ? `${def.label} sai da mensalidade, e o valor da assinatura no Asaas é refeito.`
      : `${def.label} deixa de funcionar e de ser cobrado por uso.`;
  }
  if (isSalesQuotedModule(def)) {
    return `${def.label} entra cobrando na hora, sem teste, e o valor da assinatura no Asaas é refeito.`;
  }
  return `${def.label} começa em teste se nunca foi usado. Depois do teste, entra na mensalidade.`;
};

/** Todos os módulos do catálogo, com o botão de ligar e desligar (#710). */
export const ModulesList = ({ data }: { data: ClientOverview }) => {
  const qc = useQueryClient();
  const lotId = data.lot.id;
  const statusOf = (key: string) => data.modules.find((m) => m.key === key);
  const [pending, setPending] = useState<{ def: ModuleDef; enabled: boolean } | null>(null);
  const confirm = useConfirm();

  const toggle = useMutation({
    mutationFn: (input: { key: string; enabled: boolean }) =>
      api.request(`/v1/admin/clients/${lotId}/modules/${input.key}`, {
        method: 'PATCH',
        json: { enabled: input.enabled },
      }),
    onSuccess: (_, input) => {
      toast.success(input.enabled ? 'Módulo ligado.' : 'Módulo desligado.');
      qc.invalidateQueries({ queryKey: ['admin-client-overview', lotId] });
      qc.invalidateQueries({ queryKey: ['admin-client-billing', lotId] });
      qc.invalidateQueries({ queryKey: ['admin-clients'] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível mudar o módulo.'),
  });

  return (
    <>
      <ul className="space-y-1 text-sm">
        {MODULES.map((def) => {
          const mod = statusOf(def.key);
          const status = mod?.status ?? 'off';
          const on = isOn(status);
          const ownerOnly = !on && OWNER_ONLY_ENABLE.has(def.key);
          return (
            <li key={def.key} className="flex items-center justify-between gap-2">
              <span>{def.label}</span>
              <span className="flex items-center gap-2">
                <Badge
                  variant={
                    status === 'active' ? 'success' : status === 'trialing' ? 'brand' : 'default'
                  }
                >
                  {MODULE_STATUS[status] ?? status}
                  {status === 'trialing' && mod?.trialEndsAt ? ` até ${day(mod.trialEndsAt)}` : ''}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={toggle.isPending || ownerOnly}
                  title={ownerOnly ? 'O dono liga no painel.' : undefined}
                  onClick={() => {
                    setPending({ def, enabled: !on });
                    confirm.confirm(() => toggle.mutateAsync({ key: def.key, enabled: !on }));
                  }}
                >
                  {on ? 'Desligar' : 'Ligar'}
                </Button>
              </span>
            </li>
          );
        })}
      </ul>
      <ConfirmDialog
        {...confirm.dialogProps}
        title={pending ? `${pending.enabled ? 'Ligar' : 'Desligar'} ${pending.def.label}?` : ''}
        description={pending ? effect(pending.def, pending.enabled) : ''}
        confirmLabel={pending?.enabled ? 'Ligar' : 'Desligar'}
        destructive={pending ? !pending.enabled : false}
        pending={toggle.isPending}
      />
    </>
  );
};
